
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Building, User, Eye, DollarSign, ArrowUp, ArrowDown } from "lucide-react";
import { useAdminDashboardStats } from "@/hooks/useAdminListings";

export interface AdminStatsCardsProps {
  stats?: {
    listings?: {
      total: number;
      published: number;
      draft: number;
      archived: number;
      featured: number;
      boosted: number;
      newThisMonth?: number;
    };
    users?: {
      total: number;
    };
    views?: {
      total: number;
      change: number;
    };
    value?: {
      total: number;
      change: number;
    };
  };
}

export const AdminStatsCards = () => {
  const { data: statsData, isLoading } = useAdminDashboardStats();
  
  const stats = statsData || {
    listings: {
      total: 0,
      published: 0,
      draft: 0,
      archived: 0,
      featured: 0,
      boosted: 0,
      newThisMonth: 0
    },
    users: {
      total: 0
    },
    views: {
      total: 0,
      change: 0
    },
    value: {
      total: 0,
      change: 0
    }
  };

  // Format number with thousand separators
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  // Format value as currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Total Properties Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
              <Building className="h-6 w-6 text-blue-600" />
            </div>
            <span className={`flex items-center text-sm ${stats.listings?.newThisMonth && stats.listings.newThisMonth > 0 ? 'text-green-600' : 'text-gray-600'}`}>
              {stats.listings?.newThisMonth && stats.listings.newThisMonth > 0 ? (
                <>
                  <ArrowUp className="h-3 w-3 mr-1" />
                  +{stats.listings.newThisMonth}
                </>
              ) : (
                '±0'
              )}
              <span className="ml-1">from last month</span>
            </span>
          </div>
          <CardTitle className="text-2xl font-bold">
            {isLoading ? "..." : formatNumber(stats.listings?.total || 0)}
          </CardTitle>
          <CardDescription>Total Properties</CardDescription>
        </CardContent>
      </Card>

      {/* Total Users Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100">
              <User className="h-6 w-6 text-amber-600" />
            </div>
            <span className="text-sm text-gray-600">Updated today</span>
          </div>
          <CardTitle className="text-2xl font-bold">
            {isLoading ? "..." : formatNumber(stats.users?.total || 0)}
          </CardTitle>
          <CardDescription>Total Users</CardDescription>
        </CardContent>
      </Card>

      {/* Total Views Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
              <Eye className="h-6 w-6 text-green-600" />
            </div>
            <span className={`flex items-center text-sm ${stats.views?.change && stats.views.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.views?.change && stats.views.change > 0 ? (
                <>
                  <ArrowUp className="h-3 w-3 mr-1" />
                  +{stats.views.change}%
                </>
              ) : (
                <>
                  <ArrowDown className="h-3 w-3 mr-1" />
                  {stats.views?.change || 0}%
                </>
              )}
              <span className="ml-1">from last month</span>
            </span>
          </div>
          <CardTitle className="text-2xl font-bold">
            {isLoading ? "..." : formatNumber(stats.views?.total || 1234)}
          </CardTitle>
          <CardDescription>Total Views</CardDescription>
        </CardContent>
      </Card>

      {/* Estimated Value Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-purple-100">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <span className={`flex items-center text-sm ${stats.value?.change && stats.value.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.value?.change && stats.value.change > 0 ? (
                <>
                  <ArrowUp className="h-3 w-3 mr-1" />
                  +{stats.value.change}%
                </>
              ) : (
                <>
                  <ArrowDown className="h-3 w-3 mr-1" />
                  {stats.value?.change || 0}%
                </>
              )}
              <span className="ml-1">from last month</span>
            </span>
          </div>
          <CardTitle className="text-2xl font-bold">
            {isLoading ? "..." : formatCurrency(stats.value?.total || 2400000)}
          </CardTitle>
          <CardDescription>Estimated Value</CardDescription>
        </CardContent>
      </Card>
    </div>
  );
};
